import { IInboxMessage, IOutboxMessage, denormalizeInboxMessage, normalizeOutboxMessage } from "../message";

import { IConnection, IInboxMessageSubscriber, POSTMESSAGE_UNAVAILABLE_ERROR_MESSAGE } from "./connection";
import { createSubscriberNotifier } from "./helpers";

interface IPlainConnectionOptions {
  onReconnect?(): void;
}

export async function createPlainConnection(
  iframe: HTMLIFrameElement,
  expectedOrigin: string,
  options: IPlainConnectionOptions = {}
): Promise<IConnection> {
  const child = iframe.contentWindow;
  const connectionId = String(Math.random());

  const isOwnEvent = (event: MessageEvent): boolean => {
    const { origin, source, data } = event;
    return origin === expectedOrigin && source === child && data.message && data.plugin_id;
  };

  const handshake = (): Promise<IInboxMessage> =>
    new Promise((resolve, reject) => {
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let connectionTimeout: any;

      const handleMessage = (event: MessageEvent): void => {
        if (!isOwnEvent(event)) {
          return;
        }

        clearTimeout(connectionTimeout);

        window.removeEventListener("message", handleMessage);

        const message = denormalizeInboxMessage(event.data);
        resolve(message);
      };

      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      const handleConnectionTimeout = () => {
        reject(new Error("Plain widget connection could not be established"));
      };

      window.addEventListener("message", handleMessage);

      iframe.addEventListener("load", () => {
        connectionTimeout = setTimeout(handleConnectionTimeout, 3000);
      });
    });

  const connection: IConnection = await handshake().then(initialMessage => {
    const subscribers = new Set<IInboxMessageSubscriber>();
    const notifySubscribers = createSubscriberNotifier(subscribers);

    const handleMessage = (event: MessageEvent): void => {
      if (isOwnEvent(event)) {
        notifySubscribers(denormalizeInboxMessage(event.data), connectionId);
      }
    };

    window.addEventListener("message", handleMessage);

    return {
      connectionId,
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      initialize() {
        notifySubscribers(initialMessage, connectionId);
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      subscribe(subscriber: IInboxMessageSubscriber) {
        subscribers.add(subscriber);
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      unsubscribe(subscriber: IInboxMessageSubscriber) {
        subscribers.delete(subscriber);
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      send(message: IOutboxMessage) {
        if (child && child.postMessage) {
          child.postMessage(normalizeOutboxMessage(message), expectedOrigin);
          return Promise.resolve();
        }
        return Promise.reject(new Error(POSTMESSAGE_UNAVAILABLE_ERROR_MESSAGE));
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      setContext(context) {
        // @ts-expect-error TS(2339): Property 'msgContext' does not exist on type '{ co... Remove this comment to see the full error message
        this.msgContext = context;
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      getContext() {
        // @ts-expect-error TS(2339): Property 'msgContext' does not exist on type 'ICon... Remove this comment to see the full error message
        return this.msgContext || {};
      },
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      destroy() {
        window.removeEventListener("message", handleMessage);
        subscribers.clear();
      },
    };
  });

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleReconnect = async () => {
    await handshake();
    if (options.onReconnect) {
      options.onReconnect();
    }
  };

  iframe.addEventListener("load", handleReconnect);

  return connection;
}
